import { Injectable } from "@angular/core";
import { ElementoOperacion, fila, table } from "../plantillas/models.model";
import { ConsultasService } from "./get-cuentas.service";
import moment, { Moment } from "moment";
import { formatear } from "../../../elements/funciones/funcionesGenerales";

type Token = number | string;

@Injectable({
  providedIn: 'root'
})
export class TableroService {
  private variablesRiegos = [];
  private numeroMul = 0;

  constructor(
    private readonly consultas: ConsultasService,
  ){
    formatear.momentAbrev();
  }

  async getTabla(config: table, evaluaciones: any[], idPlantilla: number, valorMul?: number){
    this.numeroMul = valorMul ?? 0;
    const configClon = structuredClone(config);
    
    const per = moment(evaluaciones[0]?.text ?? evaluaciones[0].evaluacion, 'MMM YY');
    this.variablesRiegos = await this.consultas.getRiesgos(
          configClon.id,
          per.month()+1,per.year()
        );
    const idEvaluacion = evaluaciones.flatMap( e => e?.id ?? e.idEvaluacion)[0];
    const filasResueltas = new Map<fila, fila>();
    const filasEnProceso = new Set<fila>();

    const resolverFila = async (filaConfig: fila): Promise<fila> => {
      const filaResuelta = filasResueltas.get(filaConfig);
      if (filaResuelta) {
        return filaResuelta;
      }

      if (filasEnProceso.has(filaConfig)) {
        throw new Error(
          `Dependencia circular detectada en la fila ${filaConfig.id ?? filaConfig.descripcion ?? ''}`
        );
      }

      filasEnProceso.add(filaConfig);

      try {
        let resultado: fila;

        if (filaConfig.type === 'subtotal') {
          const dependencias = configClon.filas.filter(fila =>
            filaConfig.sumar?.includes(fila.id ?? '')
          );
          const filasSumar: fila[] = [];

          for (const dependencia of dependencias) {
            filasSumar.push(await resolverFila(dependencia));
          }

          resultado = this.subtotales(filaConfig, filasSumar);
        } else {
          resultado = await this.getFila(
            filaConfig,
            idEvaluacion,
            idPlantilla,
            configClon.id,
            per,
            [...filasResueltas.values()],
          );
        }

        filasResueltas.set(filaConfig, resultado);
        return resultado;
      } finally {
        filasEnProceso.delete(filaConfig);
      }
    };

    const filas: fila[] = [];
    for (const filaConfig of configClon.filas) {
      filas.push(await resolverFila(filaConfig));
    }

    return {filas: filas, columnas: configClon.columnas};
  }

  private async getFila(
    fila: fila,
    evaluacion: number,
    idPlantilla: number,
    idInstitucion: string,
    per: Moment,
    filaPrevias: fila[],
  ){
    switch(fila.type){
      case 'cuenta':
        const val = await this.consultas.getDetalleCuenta(fila.codigo,evaluacion);
        if(!fila.descripcion){
          const des = await this.consultas.getCuenta(fila.codigo, idPlantilla);
          fila.descripcion = des;
        }
        fila = this.getBandas(fila, val);
        return fila;
      case 'variableTemp':
        const varT = await this.consultas.getDetalleVariableTemp(fila.codigo,evaluacion);
        if(fila.descripcion){
          const des = await this.consultas.getVariableTemp(fila.codigo, idPlantilla);
          fila.descripcion = des;
        }
        fila = this.getBandas(fila, varT);
        return fila;
      case 'valorComp':
        if(fila.codigo !== ''){
          const val = await this.consultas.getDetalleCuenta(fila.codigo,evaluacion);
          fila.valorComparacion = val;
        }
        const dgap = await this.getRiesgosValor(
          fila.valorComparar,
          fila.bandas
        );
        fila.bandasValue = dgap;
        fila.total = fila.bandasValue.reduce( (acc, v) => acc +=v, 0);
        return fila;
      case 'ceros':
        fila = this.getBandas(fila, 0);
        return fila;
      case 'subtotal':
        fila = this.subtotales(fila, filaPrevias);
        return fila;
      case 'operacion':
        fila = await this.evaluarOperacion(fila, filaPrevias, idInstitucion, per, evaluacion);
        return fila;
      case 'editable':
        const valEdit = await this.consultas.getDetalleVariableTemp(fila.codigo,evaluacion);
        const porcentajes = await this.getRiesgosValor(
          fila.valorComparar,
          fila.bandas
        );
        fila = this.editable(fila,valEdit,porcentajes);

        return fila;
      case 'titulo':
        return fila;
      default:
        return fila;
    }
  }
  
  private getRiesgosValor(properti: string, bandas: number[]){
    return this.variablesRiegos.find(d => d.name === properti)
      ?.bandas.map((e,i) =>  {
        var val;
        if(bandas[i]===1)
          val = e.valor;
        else 
          val = null;
        return val;
      }) ?? this.fillArray(bandas)
  }

  private fillArray(bandas: number[]){
    const bandasF = [];
    bandas.forEach(b =>{
      bandasF.push(b===1?0:null);
    });

    return bandasF;
  }

  private getBandas(fila: fila, value: number): fila{
    const banVal = [];
    fila.bandas.forEach( (b, p) => {
      if(b === 1){
        banVal.push(value*fila.calculo[p]);
      }else{
        banVal.push(null);
      }
    });

    fila.bandasValue = banVal;
    fila.total = fila.bandasValue.reduce( (acc, v) => acc +=v, 0);

    return fila;
  }

  private subtotales(fila: fila, filasAnteriores: fila[]): fila{
    const filasSumar = filasAnteriores.filter(f =>
      fila.sumar?.includes(f.id)
    );
    const bandasValue = fila.bandas.map((b, index) => {
      if (b !== 1) {
        return null;
      }
      return filasSumar.reduce((total, filaAnterior) => {
        const valor = filaAnterior.bandasValue?.[index] ?? 0;
        return total + valor;
      }, 0);
    });
    fila.bandasValue = bandasValue;
    fila.total = bandasValue.reduce(
      (acc, valor) => acc + (valor ?? 0),
      0
    );

    return fila;
  }

  private editable(fila: fila, valor: number, porcentajes: number[]): fila{
    fila.bandasValue = porcentajes.map( p => p*valor);
    fila.total = fila.bandasValue.reduce(
      (acc, valor) => acc + (valor ?? 0),
      0
    );
    return fila;
  }

  private async evaluarOperacion(
    fila: fila,
    filasAnteriores: fila[],
    idInstitucion: string,
    per: Moment,
    evaluacion: number
  ): Promise<fila> {
    const valueBan: number[] = [];
    let acumulado = 0;

    for (const operacion of fila.operaciones ?? []) {
      const tokens: Token[] = [];

      for (const elemento of operacion.expresion) {
        const expe = await this.resolverElemento(
            elemento,
            filasAnteriores,
            operacion.banda,
            acumulado,
            idInstitucion,
            per,
            fila.bandas,
            evaluacion
          )
        tokens.push(
          expe
        );
        if(elemento['valor'] !== 'acumulado' && typeof expe === 'number'){
          acumulado += expe;
        }
      }
      const val = this.evaluarExpresion(tokens);
      valueBan.push(val);
    }

    fila.bandasValue = valueBan;
    fila.total = valueBan.reduce((total, valor) => total + (valor ?? 0), 0);
    return fila;
  }

  private async resolverElemento(
    elemento: ElementoOperacion,
    filas: fila[],
    posicion: number,
    acumulado: number,
    idInstitucion: string,
    per: Moment,
    bandas: any[],
    evaluacion: number,
  ): Promise<string | number> {
    switch (elemento.tipo) {
      case 'cuenta':
        if(elemento.valor === 'acumulado'){
          return acumulado;
        }
        var cuenta = elemento.valor;
        if(elemento.valor.includes('.')){
          cuenta = elemento.valor.split('.')[0];
        }
        const bandasValue = filas.find(e => e.id === cuenta);
        if(elemento.valor.includes('.')){
          return bandasValue[elemento.valor.split('.')[1]] ?? 0
        }
        return bandasValue.bandasValue[posicion] ?? 0;
      case 'consulta':
        const bandasV = await this.getRiesgosValor(
            elemento.valor.split('.')[0],
            bandas,
          );

        return bandasV[elemento.valor.split('.')[1]] ?? 0;
      case 'numero':
        return elemento.valor;
      case 'operador':
        return elemento.valor;
      case 'parentesis':
        return elemento.valor;
      case 'numeroUsuario':
        return this.numeroMul;
      case 'consultaAT': 
        const val = await this.consultas.getDetalleCuenta(elemento.valor,evaluacion);
        return val ?? 0;
      case 'consultaATV': 
        const valV = await this.consultas.getVariable(elemento.valor,evaluacion);
        return valV ?? 0;
    }
  }

  private evaluarExpresion(tokens: Token[]): number {
    const valores: number[] = [];
    const operadores: string[] = [];

    const precedencia = (operador: string): number => {
      if (operador === '+' || operador === '-') return 1;
      if (operador === '*' || operador === '/') return 2;
      return 0;
    };

    const aplicarOperacion = () => {
      const operador = operadores.pop();
      const b = valores.pop();
      const a = valores.pop();      
      if (operador === undefined || a === undefined || b === undefined) {
        throw new Error('Expresión inválida');
      }
      switch (operador) {
        case '+':
          valores.push(a + b);
          break;
        case '-':
          valores.push(a - b);
          break;
        case '*':
          valores.push(a * b);
          break;
        case '/':
          if (b === 0) {
            // throw new Error('No se puede dividir entre cero');
            valores.push(0);
            break;
          }
          valores.push(a / b);
          break;
      }
    };
    for (const token of tokens) {
      if (typeof token === 'number') {
        valores.push(token);
        continue;
      }
      if (token === '(') {
        operadores.push(token);
        continue;
      }
      if (token === ')') {
        while (
          operadores.length > 0 &&
          operadores[operadores.length - 1] !== '('
        ) {
          aplicarOperacion();
        }
        if (operadores.pop() !== '(') {
          throw new Error('Paréntesis desbalanceados');
        }
        continue;
      }
      while (
        operadores.length > 0 &&
        operadores[operadores.length - 1] !== '(' &&
        precedencia(operadores[operadores.length - 1]) >= precedencia(token)
      ) {
        aplicarOperacion();
      }
      operadores.push(token);
    }
    while (operadores.length > 0) {
      if (operadores[operadores.length - 1] === '(') {
        throw new Error('Paréntesis desbalanceados');
      }
      aplicarOperacion();
    }
    if (valores.length !== 1) {
      throw new Error('Expresión inválida');
    }
    return valores[0];
  }
}
